import { useEffect, useRef, useState } from 'react';
import FeatherIcon from 'feather-icons-react';
import { previewPremoveoutNotice } from '../Helpers/Api';

const DEBOUNCE_MS = 350;

export const PREVIEW_TYPES = [
  { id: 'entry', label: 'Entry Notice', channel: 'Email' },
  { id: 'checklist', label: 'Checklist Email', channel: 'Email' },
  { id: 'checklist-pdf', label: 'Checklist PDF', channel: 'PDF attachment' },
];

/**
 * Shows the real notice as the server renders it for sending. The HTML comes
 * from the same template and data builder as the email, so what staff see is
 * what the resident receives. Re-renders (debounced) as the form changes.
 */
export const NoticeEmailPreview = ({ type, request, label }) => {
  const [html, setHtml] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [height, setHeight] = useState(640);
  const latest = useRef(0);
  const frame = useRef(null);
  const key = JSON.stringify({ type, ...request });

  useEffect(() => {
    const id = ++latest.current;
    setLoading(true); setError('');
    const timer = setTimeout(() => {
      previewPremoveoutNotice({ ...request, type })
        .then(result => { if (id !== latest.current) return; setHtml(result.html || ''); setLoading(false); })
        .catch(err => { if (id !== latest.current) return; setError(err.message || 'Could not render the preview.'); setLoading(false); });
    }, DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [key]); // eslint-disable-line react-hooks/exhaustive-deps

  const fit = () => {
    try {
      const doc = frame.current?.contentDocument;
      const next = doc?.documentElement?.scrollHeight || doc?.body?.scrollHeight;
      if (next) setHeight(Math.max(320, next + 2));
    } catch { /* cross-origin guard; the frame keeps its last height */ }
  };

  const meta = PREVIEW_TYPES.find(item => item.id === type) || PREVIEW_TYPES[0];
  return (
    <section className={`pm-preview-frame${type === 'checklist-pdf' ? ' is-letter' : ''}`} aria-label={label} aria-busy={loading}>
      <header className="pm-preview-toolbar">
        <span><FeatherIcon icon="eye" size={18} />{label}</span>
        <span className="pm-preview-channel"><FeatherIcon icon={type === 'checklist-pdf' ? 'paperclip' : 'mail'} size={15} />{meta.channel}</span>
      </header>
      <div className="pm-preview-paper">
        {error && <p role="alert" className="pm-preview-error">{error}</p>}
        {!error && !html && <div className="pm-loading-lines" role="status">Rendering the email...</div>}
        {!error && html && (
          <iframe
            ref={frame}
            title={label}
            className={`pm-preview-iframe${loading ? ' is-stale' : ''}`}
            sandbox="allow-same-origin allow-popups allow-popups-to-escape-sandbox"
            srcDoc={html}
            style={{ height }}
            onLoad={fit}
          />
        )}
      </div>
    </section>
  );
};
